/**
 * Whether the application cycle has closed, for the public Apply buttons
 * (Melanie, 2026-11-03: "set the button to gray").
 *
 * Reads the same public `/api/applications/gate` answer the /apply page reads
 * (`accepting_submissions: false` once the cycle's `closes_at` has passed),
 * and applies the page's own rule: `disabled` codes mode is a door staff took
 * out of the credential business, so it never shows closed. Fails OPEN: until
 * the answer arrives, or if it cannot, the button stays live and /apply, the
 * create route and the submit guard remain the enforcement.
 */
import { useEffect, useState } from 'react';
import { api } from '@/services/api';

// One gate read per page load, shared by every button that asks. A plain
// fetch rather than a query so the public chrome needs no query provider.
let pending: Promise<boolean> | null = null;
function readClosed(): Promise<boolean> {
  // Optional chaining: a page test that mocks `api` without the gate reads open.
  pending ??= Promise.resolve(api.applications?.gate?.())
    .then((res) => res?.data?.accepting_submissions === false && res?.data?.mode !== 'disabled')
    .catch(() => false);
  return pending;
}

export function useApplicationsClosed(enabled = true): boolean {
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void readClosed().then((value) => { if (live) setClosed(value); });
    return () => { live = false; };
  }, [enabled]);
  return closed;
}
